import { Box, Flex, Input, Stack, Tag, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useContext, useEffect, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { type FieldValues, type UseControllerProps } from 'react-hook-form';

import { useField } from '#features/form/hooks/useField';
import { formSelectionIconSize } from '#features/ui/theme/form-selection';
import { Button } from '../../Button';
import { Chip } from '../../Chip';
import { CloseButton } from '../../CloseButton';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
  useIsDialogSheet,
} from '../../Dialog';
import { CheckmarkIcon } from '../../Icon/Checkmark';
import { ChevronDownIcon } from '../../Icon/ChevronDown';
import { Image } from '../../Image';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '../../Popover';
import { BaseInput } from '../BaseInput';
import { OptionsGroup } from '../OptionsGroup';
import { type InputProps } from '../types';
import { GAME_PARAMETER_IMAGE_SIZES } from './imageSizes';
import { GameParameterPickerSurfaceContext } from './pickerSurface';
import { getGameParameterPresentation } from './presentation';

type Option = {
  value: string;
  label: string;
  image?: string;
  disabled?: boolean;
  secondaryText?: string;
  count?: number;
};
type Value = string | string[] | null;
type Props = Omit<InputProps<Value>, 'onBlur'> & {
  options: Option[];
  /** Short ordinary parameters use pills; long or historical lists keep the picker. */
  presentation?: 'picker' | 'auto';
  multiple?: boolean;
  maxCount?: number;
  clearable?: boolean;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Name of a picker whose visible label is supplied by the surrounding settings row. */
  triggerLabel?: string;
  /** A secondary choice can use a compact text trigger without a field border. */
  triggerVariant?: 'outline' | 'ghost';
  onBlur?: () => void;
  ref?: Ref<HTMLElement>;
};

export const GameParameterSelect = ({ ref, ...props }: Props) => {
  const isSheet = useIsDialogSheet();
  const { t } = useLingui();
  const { maxCount } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const value = props.value !== undefined ? props.value : props.defaultValue;
  const selected = [...new Set(Array.isArray(value) ? value : value ? [value] : [])];
  const showSelectionLimit =
    props.maxCount != null &&
    props.maxCount < props.options.filter((option) => !option.disabled).length;
  const presentation = getGameParameterPresentation(props.options, selected, props.presentation);
  if (presentation === 'empty') return null;
  if (presentation === 'pill') {
    const atLimit = props.multiple && props.maxCount != null && selected.length >= props.maxCount;
    return (
      <OptionsGroup
        inputRef={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        name={props.name}
        label={props.label}
        helperText={props.helperText}
        error={props.error}
        isRequired={props.isRequired}
        disabled={props.disabled}
        clearable={props.clearable && !props.isRequired}
        description={
          props.multiple && showSelectionLimit
            ? t`Selected: ${selected.length} of ${maxCount}`
            : undefined
        }
        variant="pill"
        options={props.options.map((option) => ({
          ...option,
          disabled: Boolean(option.disabled || (atLimit && !selected.includes(option.value))),
        }))}
        {...(props.multiple
          ? { selectType: 'multiple' as const, value: selected }
          : { selectType: 'single' as const, value: selected[0] ?? '' })}
        onChange={(next) => {
          if (props.disabled) return;
          if (Array.isArray(next) && props.maxCount != null && next.length > props.maxCount) return;
          props.onChange?.(next === '' ? null : next);
        }}
      />
    );
  }
  return (
    <BaseInput {...props}>
      <Picker {...props} ref={ref} isSheet={isSheet} />
    </BaseInput>
  );
};

function Picker({
  options,
  value,
  onChange,
  onBlur,
  label,
  triggerLabel,
  triggerVariant = 'outline',
  placeholder,
  searchable = options.length > 12,
  searchPlaceholder,
  multiple = false,
  maxCount,
  disabled,
  clearable,
  error,
  isRequired,
  ref,
  isSheet,
}: Props & { isSheet: boolean }) {
  const { t } = useLingui();
  const surface = useContext(GameParameterPickerSurfaceContext);
  const pickerLabel = triggerLabel ?? label;
  const selected = [...new Set(Array.isArray(value) ? value : value ? [value] : [])];
  const showSelectionLimit =
    maxCount != null && maxCount < options.filter((option) => !option.disabled).length;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isDialog = Boolean(surface) || isSheet || searchable || options.length > 12;
  const activeValues = multiple ? draft : selected;
  const selectedLabels = selected.map(
    (id) => options.find((item) => item.value === id)?.label ?? t`Unavailable option`
  );
  const selectedText =
    selected.length > 0 ? selectedLabels.join(', ') : (placeholder ?? t`form.notChosen`);
  const triggerText =
    multiple && selected.length > 0 ? t`Selected: ${selected.length}` : selectedText;
  const selectedOption = !multiple ? options.find((item) => item.value === selected[0]) : undefined;
  const visible = options.filter((item) =>
    item.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  );
  const unknown = draft.filter((id) => !options.some((item) => item.value === id));

  const changeOpen = (next: boolean) => {
    if (disabled && next) return;
    if (next) {
      setDraft(selected);
      setQuery('');
      // Opening a picker from the description must release the typing focus too.
      const focused = document.activeElement;
      if (
        focused instanceof HTMLElement &&
        focused.matches('input, textarea, [contenteditable="true"]')
      ) {
        focused.blur();
      }
    } else {
      onBlur?.();
    }
    setOpen(next);
    if (surface) {
      if (next) surface.present(() => changeOpen(false));
      else {
        surface.dismiss();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
        });
      }
    }
  };

  useEffect(() => {
    if (surface && open) contentRef.current?.focus({ preventScroll: true });
  }, [surface, open]);

  const choose = (item: Option) => {
    if (disabled || item.disabled) return;
    if (!multiple) {
      onChange?.(item.value);
      changeOpen(false);
      return;
    }
    setDraft((current) =>
      current.includes(item.value)
        ? current.filter((id) => id !== item.value)
        : maxCount != null && current.length >= maxCount
          ? current
          : [...current, item.value]
    );
  };

  const navigate = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const rows = [
      ...(listRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []),
    ];
    if (rows.length === 0) return;
    event.preventDefault();
    const current = rows.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? rows.length - 1
          : current < 0
            ? event.key === 'ArrowDown'
              ? 0
              : rows.length - 1
            : (current + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
    rows[next].focus({ preventScroll: true });
    const bounds = listRef.current!.getBoundingClientRect();
    const row = rows[next].getBoundingClientRect();
    if (row.top < bounds.top) listRef.current!.scrollTop -= bounds.top - row.top;
    if (row.bottom > bounds.bottom) listRef.current!.scrollTop += row.bottom - bounds.bottom;
  };

  const navigateFromContent = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) navigate(event);
  };

  const trigger = (
    <Button
      ref={(node) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      type="button"
      onClick={surface ? () => changeOpen(true) : undefined}
      aria-expanded={surface ? open : undefined}
      variant={triggerVariant}
      w="auto"
      flex="1 1 0%"
      minW={0}
      color={triggerVariant === 'ghost' ? 'fg.muted' : undefined}
      minH="11"
      h="auto"
      justifyContent="space-between"
      textAlign="start"
      aria-label={pickerLabel ? `${pickerLabel}: ${selectedText}` : selectedText}
      aria-invalid={Boolean(error)}
      aria-required={isRequired}
      disabled={disabled}
    >
      <Flex as="span" alignItems="center" gap={2} minW={0}>
        {selectedOption?.image && (
          <Box as="span" boxSize={5} flexShrink={0} borderRadius="l1" overflow="hidden">
            <Image
              src={selectedOption.image}
              alt=""
              width={GAME_PARAMETER_IMAGE_SIZES.trigger}
              height={GAME_PARAMETER_IMAGE_SIZES.trigger}
            />
          </Box>
        )}
        <Text
          as="span"
          minW={0}
          whiteSpace="normal"
          overflowWrap="anywhere"
          textStyle={triggerVariant === 'ghost' ? 'caption' : 'body'}
          color={triggerVariant === 'ghost' || selected.length === 0 ? 'fg.muted' : 'fg'}
        >
          {triggerText}
        </Text>
      </Flex>
      <ChevronDownIcon width="16px" height="16px" />
    </Button>
  );

  const body = (
    <>
      {searchable && (
        <Box px={3} pb={3} flexShrink={0}>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder ?? t`Search options`}
            aria-label={searchPlaceholder ?? t`Search options`}
            autoComplete="off"
          />
        </Box>
      )}
      {multiple && (
        <Text px={3} pb={2} textStyle="caption" color="fg.secondary" aria-live="polite">
          {showSelectionLimit
            ? t`Selected: ${draft.length} of ${maxCount}`
            : t`Selected: ${draft.length}`}
        </Text>
      )}
      <Stack
        ref={listRef}
        gap={1}
        px={2}
        overflowY="auto"
        overscrollBehavior="contain"
        minH={0}
        flex={isDialog ? 1 : undefined}
        maxH={isDialog ? undefined : '40vh'}
        onKeyDown={navigate}
        role="group"
        aria-label={pickerLabel}
      >
        {visible.map((item) => {
          const checked = activeValues.includes(item.value);
          const atLimit = multiple && maxCount != null && draft.length >= maxCount && !checked;
          return (
            <Button
              key={item.value}
              type="button"
              variant="ghost"
              color="fg"
              minH="11"
              h="auto"
              py={2}
              px={3}
              w="full"
              justifyContent="flex-start"
              textAlign="start"
              whiteSpace="normal"
              disabled={disabled || item.disabled || atLimit}
              aria-pressed={checked}
              bg={checked ? 'bg.selected' : undefined}
              onClick={() => choose(item)}
            >
              {item.image && (
                <Box boxSize={multiple ? 7 : 5} flexShrink={0} borderRadius="l1" overflow="hidden">
                  <Image
                    src={item.image}
                    alt=""
                    width={
                      multiple
                        ? GAME_PARAMETER_IMAGE_SIZES.multipleRow
                        : GAME_PARAMETER_IMAGE_SIZES.singleRow
                    }
                    height={
                      multiple
                        ? GAME_PARAMETER_IMAGE_SIZES.multipleRow
                        : GAME_PARAMETER_IMAGE_SIZES.singleRow
                    }
                  />
                </Box>
              )}
              <Flex
                as="span"
                flex={1}
                minW={0}
                gap={item.disabled ? 1 : 2}
                alignItems={item.disabled ? 'flex-start' : 'center'}
                flexDirection={item.disabled ? 'column' : 'row'}
                flexWrap="wrap"
              >
                <Text as="span" textStyle="body" flex={1} minW={0} overflowWrap="anywhere">
                  {item.label}
                </Text>
                {item.secondaryText &&
                  (item.disabled ? (
                    <Text
                      as="span"
                      textStyle="caption"
                      color="fg.secondary"
                      overflowWrap="anywhere"
                    >
                      {item.secondaryText}
                    </Text>
                  ) : (
                    <Chip variant="fact" size="sm" flexShrink={0}>
                      {item.secondaryText}
                    </Chip>
                  ))}
                {item.count != null && item.count > 0 && (
                  <Chip variant="fact" size="sm" flexShrink={0}>
                    {item.count}
                  </Chip>
                )}
              </Flex>
              {checked && <CheckmarkIcon width="16px" height="16px" />}
            </Button>
          );
        })}
        {multiple &&
          unknown.map((id) => (
            <Text
              key={id}
              px={3}
              py={2}
              textStyle="body"
              color="fg.muted"
            >{t`Unavailable option`}</Text>
          ))}
        {visible.length === 0 && (
          <Text p={3} textStyle="body" color="fg.muted">{t`common:notFound`}</Text>
        )}
      </Stack>
    </>
  );

  const actions = multiple && (
    <>
      <Button
        type="button"
        variant="ghost"
        minH="11"
        color="fg"
        onClick={() => changeOpen(false)}
      >{t`common:form.cancel`}</Button>
      <Button
        type="button"
        minH="11"
        disabled={disabled}
        onClick={() => {
          onChange?.(draft);
          changeOpen(false);
        }}
      >{t`Done`}</Button>
    </>
  );

  return (
    <Stack gap={2} w="full" minW={0}>
      <Flex gap={2} alignItems="center" minW={0}>
        {surface ? (
          <>
            {trigger}
            {open &&
              surface.host &&
              createPortal(
                <Flex
                  ref={contentRef}
                  tabIndex={-1}
                  data-native-back-picker="true"
                  direction="column"
                  minH={0}
                  flex={1}
                  onKeyDown={navigateFromContent}
                >
                  <DialogHeader title={pickerLabel} closable={false}>
                    <CloseButton
                      position="absolute"
                      top="50%"
                      insetEnd={3}
                      transform="translateY(-50%)"
                      minW="11"
                      minH="11"
                      aria-label={t`common:form.cancel`}
                      onClick={() => changeOpen(false)}
                    />
                  </DialogHeader>
                  <DialogBody
                    px={0}
                    py={3}
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                    minH={0}
                  >
                    {body}
                  </DialogBody>
                  {multiple && <DialogFooter>{actions}</DialogFooter>}
                </Flex>,
                surface.host
              )}
          </>
        ) : isDialog ? (
          <DialogRoot
            open={open}
            onOpenChange={(event) => changeOpen(event.open)}
            initialFocusEl="body"
            finalFocusEl={() => triggerRef.current}
            size="md"
            lazyMount
            unmountOnExit
          >
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            {/* Remove a dismissed picker immediately: a lingering exit layer can make
              delayed outside/focus events dismiss the editing dialog underneath it. */}
            {open && (
              <DialogContent
                ref={contentRef}
                onKeyDown={navigateFromContent}
                fitVisualViewport={searchable}
                h={
                  isSheet && searchable
                    ? 'calc(var(--dialog-viewport-height, 100dvh) * 0.9)'
                    : undefined
                }
              >
                <DialogHeader title={pickerLabel} closable={false}>
                  <DialogCloseTrigger
                    top="50%"
                    insetEnd={3}
                    transform="translateY(-50%)"
                    minW="11"
                    minH="11"
                  />
                </DialogHeader>
                <DialogBody
                  px={0}
                  py={3}
                  overflow="hidden"
                  display="flex"
                  flexDirection="column"
                  minH={0}
                >
                  {body}
                </DialogBody>
                {multiple && <DialogFooter>{actions}</DialogFooter>}
              </DialogContent>
            )}
          </DialogRoot>
        ) : (
          <PopoverRoot
            open={open}
            onOpenChange={(event) => changeOpen(event.open)}
            positioning={{
              sameWidth: triggerVariant !== 'ghost',
              placement: 'bottom-start',
              flip: true,
            }}
            initialFocusEl={() => contentRef.current}
            lazyMount
            unmountOnExit
          >
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            {open && (
              <PopoverContent
                ref={contentRef}
                tabIndex={-1}
                aria-label={pickerLabel}
                onKeyDown={navigateFromContent}
                w={triggerVariant === 'ghost' ? '48' : 'full'}
                p={2}
              >
                {body}
                {multiple && (
                  <Flex justifyContent="flex-end" gap={2} pt={3}>
                    {actions}
                  </Flex>
                )}
              </PopoverContent>
            )}
          </PopoverRoot>
        )}
        {!multiple && clearable && selected.length > 0 && (
          <CloseButton
            size="sm"
            minW="11"
            minH="11"
            flexShrink={0}
            disabled={disabled}
            aria-label={t`common:form.reset`}
            onClick={() => {
              onChange?.(null);
              onBlur?.();
              triggerRef.current?.focus({ preventScroll: true });
            }}
          />
        )}
      </Flex>
      {multiple && selected.length > 0 && (
        <Flex gap={2} flexWrap="wrap" alignItems="center" minW={0}>
          {selected.map((id, index) => {
            const item = options.find((option) => option.value === id);
            const itemLabel = selectedLabels[index];
            return (
              <Tag.Root
                key={id}
                size={'form' as 'lg'}
                variant={'selection' as 'surface'}
                data-disabled={disabled ? '' : undefined}
                maxW="full"
                pe={clearable ? 0 : undefined}
              >
                {item?.image && (
                  <Tag.StartElement
                    boxSize={formSelectionIconSize}
                    flexShrink={0}
                    borderRadius="l1"
                    overflow="hidden"
                  >
                    <Image
                      src={item.image}
                      alt=""
                      width={GAME_PARAMETER_IMAGE_SIZES.selectedTag}
                      height={GAME_PARAMETER_IMAGE_SIZES.selectedTag}
                    />
                  </Tag.StartElement>
                )}
                <Tag.Label lineClamp="none" whiteSpace="normal" overflowWrap="anywhere">
                  {itemLabel}
                </Tag.Label>
                {clearable && (
                  <Tag.EndElement boxSize="11" me={0} flexShrink={0}>
                    <CloseButton
                      size="sm"
                      borderRadius="full"
                      minW="11"
                      minH="11"
                      disabled={disabled}
                      aria-label={t`Remove ${itemLabel}`}
                      onClick={() => {
                        onChange?.(selected.filter((selectedId) => selectedId !== id));
                        onBlur?.();
                        triggerRef.current?.focus({ preventScroll: true });
                      }}
                    />
                  </Tag.EndElement>
                )}
              </Tag.Root>
            );
          })}
        </Flex>
      )}
    </Stack>
  );
}

export type GameParameterSelectFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<Props, 'value' | 'defaultValue' | 'ref' | 'onChange'> &
  Pick<Props, 'onChange'>;

export const GameParameterSelectField = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  onChange,
  rules,
  shouldUnregister,
  ...props
}: GameParameterSelectFieldProps<T>) => {
  const field = useField({ name, control, defaultValue, onChange, rules, shouldUnregister });
  return <GameParameterSelect {...field} {...props} />;
};
